import {Box, Paper, Stack, Typography, useTheme} from '@mui/material';
import {HS_PROTOCOL_REGIONS} from '../regions';
import type {RegionStatus} from './RegionSidebar';

/**
 * SVG path data for each HS protocol region, split between front and back
 * body views. Taken from the sales director's validated mockup.
 */
const REGION_PATHS: Record<string, {path: string}> = {
  face: {
    path: 'M 95,22 C 95,14 100,8 105,6 C 110,4 115,4 120,6 C 125,8 130,14 130,22 C 130,30 125,36 120,38 C 115,40 110,40 105,38 C 100,36 95,30 95,22 Z'
  },
  neck: {
    path: 'M 105,38 L 105,50 L 120,50 L 120,38 Z'
  },
  'left-axilla': {
    path: 'M 80,54 L 90,50 L 95,61 L 85,65 Z'
  },
  'right-axilla': {
    path: 'M 130,50 L 145,54 L 140,65 L 130,61 Z'
  },
  'chest-inframammary': {
    path: 'M 95,54 L 130,54 L 130,76 L 95,76 Z'
  },
  abdomen: {
    path: 'M 95,76 L 130,76 L 130,98 L 95,98 Z'
  },
  back: {
    path: 'M 160,54 L 195,54 L 195,98 L 160,98 Z'
  },
  'left-buttock': {
    path: 'M 160,98 L 177,98 L 177,116 L 160,116 Z'
  },
  'right-buttock': {
    path: 'M 178,98 L 195,98 L 195,116 L 178,116 Z'
  },
  'left-inguinal': {
    path: 'M 95,98 L 110,98 L 108,120 L 93,120 Z'
  },
  'right-inguinal': {
    path: 'M 115,98 L 130,98 L 132,120 L 117,120 Z'
  },
  genital: {
    path: 'M 105,94 L 120,94 L 120,105 L 105,105 Z'
  }
};

const FRONT_REGIONS = new Set([
  'face',
  'neck',
  'left-axilla',
  'right-axilla',
  'chest-inframammary',
  'abdomen',
  'left-inguinal',
  'right-inguinal',
  'genital'
]);
const BACK_REGIONS = new Set(['back', 'left-buttock', 'right-buttock']);

interface BodyRegionGuideProps {
  currentRegionId: string;
  getRegionStatus: (regionId: string) => RegionStatus;
  onSelectRegion: (regionId: string) => void;
}

export default function BodyRegionGuide({
  currentRegionId,
  getRegionStatus,
  onSelectRegion
}: BodyRegionGuideProps) {
  const theme = useTheme();

  function getColor(regionId: string) {
    if (regionId === currentRegionId) {
      return theme.palette.primary.main;
    }
    const status = getRegionStatus(regionId);
    if (status === 'complete') {
      return theme.palette.success.main;
    }
    if (status === 'skipped') {
      return theme.palette.grey[400];
    }
    return 'transparent';
  }

  function getOpacity(regionId: string) {
    if (regionId === currentRegionId) {
      return 0.5;
    }
    const status = getRegionStatus(regionId);
    if (status === 'complete') {
      return 0.45;
    }
    if (status === 'skipped') {
      return 0.2;
    }
    return 0;
  }

  function renderRegionPaths(regionIds: Set<string>) {
    return Object.entries(REGION_PATHS)
      .filter(([id]) => regionIds.has(id))
      .map(([id, {path}]) => (
        <path
          key={id}
          d={path}
          fill={getColor(id)}
          opacity={getOpacity(id)}
          stroke={id === currentRegionId ? theme.palette.primary.main : 'transparent'}
          strokeWidth={id === currentRegionId ? 2 : 0}
          cursor="pointer"
          onClick={() => {
            const idx = HS_PROTOCOL_REGIONS.findIndex((r) => r.id === id);
            if (idx >= 0) {
              onSelectRegion(id);
            }
          }}
        >
          <title>{HS_PROTOCOL_REGIONS.find((r) => r.id === id)?.name}</title>
        </path>
      ));
  }

  const outlineColor = theme.palette.divider;

  return (
    <Paper variant="outlined" sx={{p: 1.5}}>
      <Typography
        variant="overline"
        sx={{fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5, px: 0.5}}
      >
        Body Region Guide
      </Typography>
      <Stack direction="row" spacing={1} justifyContent="center">
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.25}}>
            Front
          </Typography>
          <svg viewBox="60 0 105 200" width="100%" height="auto">
            <g stroke={outlineColor} strokeWidth="1" fill="none">
              <ellipse cx="112" cy="22" rx="18" ry="18" />
              <line x1="105" y1="38" x2="105" y2="50" />
              <line x1="120" y1="38" x2="120" y2="50" />
              <path d="M 90,50 L 95,50 L 95,102 L 90,120 L 93,180 L 100,180 L 105,120 L 112,109 L 120,120 L 125,180 L 132,180 L 135,120 L 130,102 L 130,50 L 135,50" />
              <path d="M 90,50 L 75,54 L 68,76 L 62,102 L 68,103 L 76,79 L 85,65" />
              <path d="M 135,50 L 150,54 L 157,76 L 163,102 L 157,103 L 149,79 L 140,65" />
            </g>
            {renderRegionPaths(FRONT_REGIONS)}
          </svg>
        </Box>
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.25}}>
            Back
          </Typography>
          <svg viewBox="140 0 105 200" width="100%" height="auto">
            <g stroke={outlineColor} strokeWidth="1" fill="none">
              <ellipse cx="177" cy="22" rx="18" ry="18" />
              <line x1="170" y1="38" x2="170" y2="50" />
              <line x1="185" y1="38" x2="185" y2="50" />
              <path d="M 155,50 L 160,50 L 160,102 L 155,120 L 158,180 L 165,180 L 170,120 L 177,109 L 185,120 L 190,180 L 197,180 L 200,120 L 195,102 L 195,50 L 200,50" />
              <path d="M 155,50 L 140,54 L 133,76 L 127,102 L 133,103 L 141,79 L 150,65" />
              <path d="M 200,50 L 215,54 L 222,76 L 228,102 L 222,103 L 214,79 L 205,65" />
            </g>
            {renderRegionPaths(BACK_REGIONS)}
          </svg>
        </Box>
      </Stack>
    </Paper>
  );
}
